/**
 * «خرید / فروش / تبدیل» (Phase 6; PRODUCT_REQUIREMENTS §9.4). Completed conversions are informational
 * for operations (they completed internally at the quoted amounts). The treasury note (BOUGHT /
 * NOT_BOUGHT + actual external price) is for estimated-profit reporting only — it never touches the
 * ledger and never alters the user's result.
 */
import {
  AppContainer,
  Badge,
  Button,
  DataTable,
  PageHeader,
  SelectField,
  StateView,
  TextField,
  formatDecimal,
  formatToman,
} from '@hopex/ui';
import { useState } from 'react';
import { api, dt, type Paged } from '../../api.ts';
import { CommandDialog, Facts } from '../../lib/command.tsx';
import { Remote, useRemote } from '../../lib/remote.tsx';
import { Pager, UserCell } from '../money/shared.tsx';

interface Conversion {
  id: string;
  ref: string;
  route: 'BUY' | 'SELL' | 'USDT_TO_COIN' | 'COIN_TO_USDT';
  source: { asset: string; amount: string };
  dest: { asset: string; amount: string };
  baseRate: string;
  fee: { asset: string; amount: string };
  treasury: { state: 'INTERNAL_UNRECORDED' | 'BOUGHT' | 'NOT_BOUGHT'; actualPrice: string | null };
  createdAt: string;
  userId: string;
  publicId: string | null;
  displayName: string | null;
}

const ROUTE: Record<Conversion['route'], string> = {
  BUY: 'خرید',
  SELL: 'فروش',
  USDT_TO_COIN: 'تبدیل تتر به رمزارز',
  COIN_TO_USDT: 'تبدیل رمزارز به تتر',
};
const TREASURY: Record<string, { label: string; tone: 'neutral' | 'positive' | 'accent' }> = {
  INTERNAL_UNRECORDED: { label: 'ثبت نشده', tone: 'neutral' },
  BOUGHT: { label: 'خریداری شد', tone: 'positive' },
  NOT_BOUGHT: { label: 'خریداری نشد', tone: 'accent' },
};
const amt = (a: { asset: string; amount: string }) =>
  a.asset === 'IRT'
    ? `${formatToman(a.amount)} تومان` // whole Tomans everywhere (owner 2026-10-15)
    : `${formatDecimal(a.amount, { trimTrailingZeros: true })} ${a.asset}`;

export function ConversionsPage() {
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<Conversion | null>(null);
  const [state, setState] = useState('BOUGHT');
  const [price, setPrice] = useState('');
  /** Quote unit of the recorded price; defaults to the order's base-rate unit (BUY → IRT, else USDT). */
  const [unit, setUnit] = useState<'IRT' | 'USDT'>('IRT');
  const r = useRemote<Paged<Conversion>>(`/conversions?page=${page}`);
  return (
    <AppContainer>
      <PageHeader title="خرید / فروش / تبدیل" />
      <Remote r={r}>
        {(d) => (
          <div className="flex flex-col gap-3" data-conversions>
            <DataTable<Conversion>
              label="تبدیل‌ها"
              rows={d.items}
              density="compact"
              stickyFirst
              columns={[
                {
                  id: 'ref',
                  header: 'کد',
                  isRowHeader: true,
                  cell: (c) => (
                    <bdi dir="ltr" className="font-mono text-[12px]">
                      {c.ref}
                    </bdi>
                  ),
                },
                {
                  id: 'user',
                  header: 'کاربر',
                  cell: (c) => (
                    <UserCell userId={c.userId} name={c.displayName} publicId={c.publicId} />
                  ),
                },
                { id: 'route', header: 'نوع', cell: (c) => ROUTE[c.route] },
                {
                  id: 'src',
                  header: 'پرداختی',
                  numeric: true,
                  cell: (c) => (
                    <bdi dir="ltr" className="font-mono text-[12px]">
                      {amt(c.source)}
                    </bdi>
                  ),
                },
                {
                  id: 'dst',
                  header: 'دریافتی',
                  numeric: true,
                  cell: (c) => (
                    <bdi dir="ltr" className="font-mono text-[12px]">
                      {amt(c.dest)}
                    </bdi>
                  ),
                },
                {
                  id: 'fee',
                  header: 'کارمزد',
                  numeric: true,
                  cell: (c) => (
                    <bdi dir="ltr" className="font-mono text-[12px]">
                      {amt(c.fee)}
                    </bdi>
                  ),
                },
                {
                  id: 'tr',
                  header: 'خزانه',
                  cell: (c) => (
                    <Badge tone={TREASURY[c.treasury.state]?.tone ?? 'neutral'}>
                      {TREASURY[c.treasury.state]?.label}
                    </Badge>
                  ),
                },
                {
                  id: 'at',
                  header: 'زمان',
                  cell: (c) => <span className="text-[12px]">{dt(c.createdAt)}</span>,
                },
                {
                  id: 'act',
                  header: 'یادداشت خزانه',
                  cell: (c) => (
                    <Button
                      size="sm"
                      variant="ghost"
                      onPress={() => (setUnit(c.route === 'BUY' ? 'IRT' : 'USDT'), setOpen(c))}
                    >
                      ثبت
                    </Button>
                  ),
                },
              ]}
              empty={<StateView compact kind="empty" title="تبدیلی ثبت نشده است" />}
            />
            <Pager page={d.page} hasMore={d.hasMore} onPage={setPage} />
          </div>
        )}
      </Remote>
      {open && (
        <CommandDialog
          title={`یادداشت خزانه — ${open.ref}`}
          canSubmit={state === 'NOT_BOUGHT' || /^\d+(\.\d+)?$/.test(price)}
          run={async (reason) =>
            void (await api.post(`/conversions/${open.id}/treasury`, {
              state,
              ...(state === 'BOUGHT' ? { actualPrice: price, priceUnit: unit } : {}),
              reason,
            }))
          }
          onClose={() => setOpen(null)}
          onDone={() => (setOpen(null), r.reload())}
        >
          <Facts
            rows={[
              ['اثر', 'فقط برای گزارش سود تخمینی؛ موجودی و نتیجه‌ی کاربر تغییر نمی‌کند.'],
              ['تبدیل', `${amt(open.source)} ← ${amt(open.dest)}`],
            ]}
          />
          <SelectField
            label="وضعیت"
            value={state}
            onChange={setState}
            options={[
              { value: 'BOUGHT', label: 'خریداری شد' },
              { value: 'NOT_BOUGHT', label: 'خریداری نشد' },
            ]}
          />
          {state === 'BOUGHT' && (
            <>
              <TextField
                label="قیمت واقعی خرید بیرونی (هر واحد)"
                value={price}
                onChange={setPrice}
                ltr
              />
              <SelectField
                label="واحد قیمت"
                value={unit}
                onChange={(v) => setUnit(v as 'IRT' | 'USDT')}
                options={[
                  { value: 'IRT', label: 'تومان' },
                  { value: 'USDT', label: 'تتر (USDT)' },
                ]}
              />
            </>
          )}
        </CommandDialog>
      )}
    </AppContainer>
  );
}
